import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import JsonLd from '@/components/base/JsonLd';
import { updateDocumentMeta } from '@/components/base/SeoHelmet';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  const categoryName = useMemo(() => {
    if (!article) return '';
    return categories.find((c) => c.slug === article.category)?.name || '';
  }, [article]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  const currentIndex = useMemo(() => {
    if (!article) return -1;
    return articles.findIndex((a) => a.id === article.id);
  }, [article]);

  const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : null;
  const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null;

  useEffect(() => {
    if (article) {
      updateDocumentMeta(
        `${article.title} - 红桃视频在线观看`,
        `红桃视频在线观看为您带来：${article.title}。${article.excerpt}高清画质，流畅播放，尽在红桃视频在线观看。`,
        `红桃视频在线观看,红桃视频免费观看,${article.title},红桃视频高清播放,${categoryName}在线观看`,
        `https://www.hongtaovideo.com/article/${article.slug}`
      );
    }
  }, [article, categoryName]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-background-200 flex items-center justify-center mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900">文章未找到</h1>
            <p className="mt-2 text-sm text-foreground-500">该视频内容不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '红桃视频在线观看',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hongtaovideo.com/' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `https://www.hongtaovideo.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.hongtaovideo.com/article/${article.slug}` },
    ],
  };

  return (
    <>
      <JsonLd data={articleJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: categoryName, to: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <article className="pb-14 md:pb-20">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                  {categoryName}
                </span>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {article.views.toLocaleString()} 次播放
                  </span>
                </div>
              </div>

              <div className="mb-8 rounded-lg overflow-hidden aspect-video">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  title={`${article.title} - 红桃视频在线观看`}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                  [&>h2]:font-heading [&>h2]:text-xl [&>h2]:font-bold [&>h2]:text-foreground-900 [&>h2]:mt-8 [&>h2]:mb-4
                  [&>p]:mb-4 [&>p]:leading-relaxed
                  [&_a]:text-primary-600 [&_a]:no-underline hover:[&_a]:underline"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 p-4 md:p-5 rounded-lg bg-background-100 border border-background-200">
                <p className="text-sm text-foreground-600">
                  <strong>免责声明：</strong>红桃视频在线观看提供的内容仅供个人学习、研究和欣赏使用。版权归原作者所有，如需商业使用请联系版权方获取授权。
                </p>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                {prevArticle ? (
                  <Link
                    to={`/article/${prevArticle.slug}`}
                    className="flex items-center gap-2 px-4 py-3 rounded-lg border border-background-200 hover:border-primary-200 hover:bg-primary-50 transition-all group flex-1"
                  >
                    <i className="ri-arrow-left-line text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                    <div className="min-w-0">
                      <div className="text-xs text-foreground-400">上一篇</div>
                      <div className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors truncate">
                        {prevArticle.title}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="flex-1"></div>
                )}
                {nextArticle ? (
                  <Link
                    to={`/article/${nextArticle.slug}`}
                    className="flex items-center justify-end gap-2 px-4 py-3 rounded-lg border border-background-200 hover:border-primary-200 hover:bg-primary-50 transition-all group flex-1"
                  >
                    <div className="min-w-0 text-right">
                      <div className="text-xs text-foreground-400">下一篇</div>
                      <div className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors truncate">
                        {nextArticle.title}
                      </div>
                    </div>
                    <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                  </Link>
                ) : (
                  <div className="flex-1"></div>
                )}
              </div>

              {relatedArticles.length > 0 && (
                <section className="mt-14">
                  <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-5">
                    相关推荐
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedArticles.map((rel) => (
                      <Link
                        key={rel.id}
                        to={`/article/${rel.slug}`}
                        className="group block rounded-lg border border-background-200 overflow-hidden hover:border-primary-200 transition-all"
                      >
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={rel.thumbnail}
                            alt={rel.title}
                            title={`${rel.title} - 红桃视频在线观看`}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-3">
                          <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {rel.title}
                          </h4>
                          <p className="mt-1 text-xs text-foreground-500">
                            {rel.views.toLocaleString()} 次播放
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}